(() => {
  if (customElements.get("miranda-native-crank-finder")) return;
  const esc = (value) =>
    String(value ?? "").replace(
      /[&<>"']/g,
      (c) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[c],
    );
  class CrankFinder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.sequence = 0;
      this.state = {
        view: "home",
        query: "",
        bike: null,
        year: "",
        size: "",
        setup: "",
      };
      this.addEventListener("click", (event) => this.click(event));
      this.addEventListener("submit", (event) => this.submit(event));
      this.addEventListener("input", () => this.captureDraft());
      this.addEventListener("change", () => this.captureDraft());
      this.home();
    }
    disconnectedCallback() {
      this.cancel();
    }
    cancel() {
      this.sequence++;
      this.controller?.abort();
      this.removeAttribute("aria-busy");
      this.retryWork = null;
    }
    active(signal) {
      if (signal?.aborted) throw new DOMException("Request cancelled", "AbortError");
    }
    captureDraft(captureMotor = true) {
      const setup = this.querySelector('form[data-form="setup"]');
      if (setup) {
        const year = setup.querySelector('[name="year"]');
        const size = setup.querySelector('[name="size"]');
        if (year) this.state.year = year.value;
        if (size) this.state.size = size.value;
      }
      const sensor = this.querySelector('form[data-form="sensor-marking"] [name="marking"]');
      if (captureMotor && sensor) this.state.sensorMarking = sensor.value;
      const part = this.querySelector('form[data-form="part"] [name="q"]');
      if (part && part.value !== this.state.partQuery) {
        this.state.partQuery = part.value;
        this.state.part = null;
      }
      const motor = this.querySelector('form[data-form="motor-marking"] [name="marking"]');
      if (captureMotor && motor) this.state.motorMarking = motor.value;
    }
    validYear(value) {
      return String(value ?? "").trim() !== "" && Number.isInteger(Number(value)) && Number(value) >= 1990 && Number(value) <= 2100;
    }
    shell(content, view = "flow") {
      this.dataset.step = view;
      this.innerHTML = `<div class="mcf-frame"><div class="mcf-top"><img src="${esc(this.dataset.logo)}" alt="Miranda" width="123" height="30"><span>CRANK FINDER</span><button type="button" data-action="help">Need help? ↗</button></div><div class="mcf-layout"><section class="mcf-content" aria-label="Crank finder">${content}<p class="mcf-status" role="status" aria-live="polite"></p></section><aside class="mcf-stage" aria-label="Miranda Kappa crank"><span class="mcf-watermark" aria-hidden="true">KAPPA</span><div class="mcf-halo"></div><img src="${esc(this.dataset.image)}" alt="Black Miranda Kappa RF ISIS crank" width="1137" height="1137"><div class="mcf-caption"><b>MIRANDA KAPPA</b><span>RF ISIS / Precision in every detail.</span></div><small>Product shown for illustration.</small></aside></div><div class="mcf-bottom"><span>DESIGNED & MANUFACTURED IN PORTUGAL</span><span>Identify your motor <i>→</i> See your options <i>→</i> Choose your version</span></div></div>`;
    }
    heading(kicker, title, description = "") {
      return `<p class="mcf-kicker">${esc(kicker)}</p><h2 tabindex="-1">${title}</h2>${description ? `<p class="mcf-description">${esc(description)}</p>` : ""}`;
    }
    bikeMeta(b) {
      return [
        b.year || "Model year to confirm",
        b.code ? `Code ${b.code}` : "",
        b.market,
        b.version && b.version !== b.model ? b.version : "",
        b.motor,
      ]
        .filter(Boolean)
        .map(esc)
        .join(" · ");
    }
    back(action = "home") {
      return `<button class="mcf-back" type="button" data-action="${action}">← Back</button>`;
    }
    focus() {
      this.querySelector("h2")?.focus({ preventScroll: true });
      const content = this.querySelector(".mcf-content");
      const top = content?.getBoundingClientRect().top;
      if (typeof this.scrollIntoView === "function" && (top < 0 || top > window.innerHeight * 0.7)) {
        this.scrollIntoView({block: "start", behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"});
      }
    }
    status(message) {
      const el = this.querySelector(".mcf-status");
      if (el) el.textContent = message;
    }
    async loadNative(kind, force = false, signal) {
      this.active(signal);
      this.nativeCache ||= {};
      if (!force && this.nativeCache[kind]) return this.nativeCache[kind];
      const selector = `script[data-mcf-native="${kind}"]`;
      const initial = document.querySelector(selector);
      if (!initial?.dataset.sectionId) throw new Error("The finder is temporarily unavailable. Please contact Miranda.");
      const sectionId = initial.dataset.sectionId;
      const fetchPage = async (url) => {
        if (url.origin !== location.origin || url.pathname !== location.pathname) throw new Error("The finder data could not be loaded.");
        url.searchParams.set("section_id", sectionId);
        const previewId = new URL(location.href).searchParams.get("preview_theme_id");
        if (previewId) url.searchParams.set("preview_theme_id", previewId);
        const templateView = new URL(location.href).searchParams.get("view");
        if (templateView) url.searchParams.set("view", templateView);
        this.active(signal);
        const response = await fetch(url, { credentials: "same-origin", cache: "no-store", signal });
        if (!response.ok) throw new Error("The finder is temporarily unavailable. Please try again.");
        const html = await response.text();
        this.active(signal);
        const doc = new DOMParser().parseFromString(html, "text/html");
        const script = doc.querySelector(selector);
        if (!script) throw new Error("The finder data could not be loaded. Please try again.");
        return JSON.parse(script.textContent);
      };
      let payload;
      if (force) {
        const start = new URL(location.href);
        start.searchParams.delete("page");
        payload = await fetchPage(start);
      } else payload = JSON.parse(initial.textContent);
      const rows = [];
      const visited = new Set();
      const enabled = payload.enabled === true;
      const total = payload.total;
      for (let page = 0; page < 500; page++) {
        this.active(signal);
        if (payload.total !== total || (payload.enabled === true) !== enabled) throw new Error("The finder data changed. Please try again.");
        if (!Array.isArray(payload[kind])) throw new Error("The finder data could not be loaded.");
        rows.push(...payload[kind]);
        if (!payload.next) {
          if (!Number.isInteger(total) || rows.length !== total || new Set(rows.map(x => x.id)).size !== rows.length) throw new Error("The finder data changed. Please reload this page and try again.");
          const data = { rows, enabled };
          this.nativeCache[kind] = data;
          return data;
        }
        const next = new URL(payload.next, location.href);
        if (visited.has(next.href)) throw new Error("The finder data could not be loaded.");
        visited.add(next.href);
        payload = await fetchPage(next);
      }
      throw new Error("The finder data could not be loaded. Please contact Miranda.");
    }
    async loadParts(signal) {
      this.active(signal);
      if (this.partsCache) return this.partsCache;
      const url = new URL(this.dataset.parts, location.origin);
      if (url.protocol !== "https:" && url.origin !== location.origin) throw new Error("Part identification is temporarily unavailable.");
      const response = await fetch(url, { cache: "no-store", signal });
      if (!response.ok) throw new Error("Part identification is temporarily unavailable. Please try again.");
      const data = await response.json();
      this.active(signal);
      if (!Array.isArray(data.parts) || !Array.isArray(data.motors)) throw new Error("Part identification is temporarily unavailable.");
      this.partsCache = data;
      return data;
    }
    async loadSuggestions(signal) {
      this.active(signal);
      if (!this.dataset.suggestions || this.dataset.suggestionsEnabled === "false") return {bikes: [], motors: []};
      if (this.suggestionsCache) return this.suggestionsCache;
      const url = new URL(this.dataset.suggestions, location.origin);
      if (url.protocol !== "https:" && url.origin !== location.origin) throw new Error("Crank options could not be loaded.");
      const response = await fetch(url, {cache: "no-store", signal});
      if (!response.ok) throw new Error("Crank options could not be loaded. Please try again.");
      const data = await response.json();
      this.active(signal);
      if (this.dataset.suggestionsEnabled === "false") return {bikes: [], motors: []};
      if (data.version !== 3 || !Array.isArray(data.bikes) || !Array.isArray(data.motors)) throw new Error("Crank options could not be loaded.");
      if (!data.products || typeof data.products !== "object") throw new Error("Crank options could not be loaded.");
      for (const record of [...data.bikes, ...data.motors]) {
        if (!Array.isArray(record.products) || record.products.some(key => !data.products[key])) throw new Error("Crank options could not be loaded.");
        record.products = record.products.map(key => data.products[key]);
      }
      this.suggestionsCache = data;
      return data;
    }

    async suggestionFor(bike, year, signal) {
      const data = await this.loadSuggestions(signal);
      const record = data.bikes.find(x => x.bike?.id === bike.id);
      if (!record) return null;
      const keys = ["id", "brand", "model", "year", "code", "version", "market", "motor"];
      if (keys.some(k => this.stable(record.bike[k] ?? null) !== this.stable(bike[k] ?? null))) return null;
      const yearKnown = this.validYear(year);
      if (yearKnown && bike.year && Number(year) !== Number(bike.year)) return null;
      if (record.blockedReason) return record;
      if (record.blockedYears?.length && (!yearKnown || record.blockedYears.includes(Number(year)))) {
        return {...record, products: [], blockedReason: yearKnown ? record.yearBlockReason : "This bicycle has a model-year-specific mounting check. Identify the year or use your current crank reference before choosing a replacement."};
      }
      if (yearKnown) return record;
      if (record.basis === "MODEL_SPECIFIC_CATALOGUE" || record.group === "LEVO3") {
        return {...record, products: [], blockedReason: "This model uses a specific left/right crank pair. Confirm the model year or use the full code from your existing crank."};
      }
      const generic = data.motors.filter(m => m.group === record.group && m.group !== "UNKNOWN" && !m.qLabel && !m.blockedReason && m.basis !== "MODEL_SPECIFIC_CATALOGUE" && m.basis !== "IDENTIFICATION_ONLY" && m.systemType !== "torque_sensor" && m.motorLocation !== "hub");
      const products = new Map();
      for (const profile of generic) for (const product of profile.products) {
        const previous = products.get(product.handle);
        const variants = new Map([...(previous?.variants || []), ...product.variants].map(v => [String(v.id), v]));
        products.set(product.handle, {...product, variants: [...variants.values()]});
      }
      return {...record, products: [...products.values()], qLabel: null, factoryCrank: "", factoryLengths: [], basis: "MOTOR_OR_INTERFACE", yearUnconfirmed: true};
    }
    async liveProduct(handle, expected, signal) {
      this.active(signal);
      const response = await fetch(
        `${this.dataset.root || "/"}products/${encodeURIComponent(handle)}.js`,
        {credentials: "same-origin", cache: "no-store", signal},
      );
      if (response.status >= 500) throw new Error("The live catalogue is temporarily unavailable. Please try again.");
      if (!response.ok) return null;
      const product = await response.json();
      this.active(signal);
      if (product.handle !== handle || !Array.isArray(product.variants)) return null;
      const variants = expected.filter(v => product.variants.some(x =>
        String(x.id) === String(v.id).split("/").pop() && x.sku === v.sku &&
        this.stable(x.options || [x.option1, x.option2, x.option3].filter(Boolean)) === this.stable(v.options)
      )).map(v => ({...v, id: String(v.id).split("/").pop(), available: product.variants.find(x => String(x.id) === String(v.id).split("/").pop()).available === true}));
      return {product, variants};
    }

    async showSuggestions(profile, current, label, signal) {
      const checked = await Promise.all(profile.products.map(async p => {
        try { return {p, live: await this.liveProduct(p.handle, p.variants, signal)}; }
        catch (error) { if (error.name === "AbortError") throw error; return {p, live: null, error}; }
      }));
      if (!current()) return;
      if (this.dataset.suggestionsEnabled === "false") {
        this.needsReview("Catalogue check", label, "Catalogue options are currently paused. Use your current crank code or ask us to check.");
        return;
      }
      const cards = checked.filter(x => x.live?.variants.length).map(({p, live}) => {
        const available = live.variants.filter(v => v.available);
        const base = `${this.dataset.root || "/"}products/${encodeURIComponent(p.handle)}`;
        const lengths = profile.factoryLengths || [];
        const preferred = profile.qLabel ? available.find(v => lengths.some(length => String(length) === v.options[0])) : null;
        const options = profile.qLabel ? available.map(v => `<a class="mcf-size-link" href="${esc(base)}?variant=${encodeURIComponent(v.id)}">${esc(v.options[0])} mm</a>`).join("") : "";
        const image = live.product.featured_image || live.product.images?.[0];
        return `<article class="mcf-recommendation">${image ? `<img class="mcf-product-image" src="${esc(image)}" alt="${esc(p.title)}" width="100" height="100" loading="lazy">` : ""}<span class="mcf-tag">${profile.basis === "MODEL_SPECIFIC_CATALOGUE" ? "MODEL-SPECIFIC OPTION" : profile.qLabel ? "MATCHING CRANK SHAPE" : profile.systemType === "torque_sensor" ? "SENSOR / INTERFACE OPTION" : "MOTOR / INTERFACE OPTION"}</span><h3>${esc(p.title)}</h3><p>${profile.qLabel ? `Matching crank marking: ${esc(profile.qLabel)}` : "Choose the length and crank shape on the product page."}</p>${preferred ? `<p class="mcf-note">Recorded original length: ${esc(preferred.options[0])} mm.</p>` : profile.qLabel && lengths.length ? `<p class="mcf-note">The recorded original length is unavailable. Check a different length before ordering.</p>` : ""}${options ? `<div class="mcf-size-options" aria-label="Available crank lengths">${options}</div>` : ""}${available.length ? (profile.qLabel && !preferred ? '<p class="mcf-note">Choose one of the available lengths above.</p>' : `<a class="mcf-button" href="${esc(preferred ? `${base}?variant=${encodeURIComponent(preferred.id)}` : base)}">${preferred ? "View original length ↗" : "Choose options ↗"}</a>`) : '<p>Currently unavailable in these crank options.</p><button class="mcf-button" type="button" data-action="help">Ask about a replacement ↗</button>'}</article>`;
      });
      if (!cards.length) {
        if (checked.some(x => x.error)) throw new Error("We could not check the live catalogue. Please try again.");
        this.needsReview("One more check", label, profile.blockedReason || "We could not confirm a current product for this selection. Identify your existing crank or send us the details below.");
        return;
      }
      this.state.view = "suggestions";
      this.state.suggestionProfile = profile;
      this.state.suggestionLabel = label;
      this.shell(`${this.back(profile.systemType === "torque_sensor" ? "sensors" : this.state.motorId ? "motor" : this.state.bike ? "setup" : "motor")}${this.heading("03 / YOUR OPTIONS", "Your crank options.", label)}${this.context()}${profile.factoryCrank ? `<details class="mcf-factory"><summary>Recorded factory setup</summary><p>${esc(profile.factoryCrank)}</p></details>` : ""}<p class="mcf-note mcf-fit-note">These are catalogue options, not a confirmed frame fit. Before ordering, match the length and crank shape to your bike.</p><div class="mcf-recommendations">${cards.join("")}</div><div class="mcf-guide"><h3>Need help choosing the version?</h3><p>Your bike may need a different crank profile on each side. Match the complete pair, including any prefixes or suffixes.</p><a class="mcf-q-link" href="${esc(this.root)}pages/crank-q-factor-guide" target="_blank" rel="noopener">Understand left and right Q-profiles ↗</a><button class="mcf-button" type="button" data-action="geometry-guide">Show me where to look ↗</button></div><div class="mcf-route-links"><button type="button" data-action="${profile.systemType === "torque_sensor" ? "sensors" : "motor"}">Change or identify ${profile.systemType === "torque_sensor" ? "sensor" : "motor"} ↗</button><button type="button" data-action="part">Use an existing part code ↗</button></div>`, "suggestions");
      this.focus();
    }

    chooseMotor(id) {
      this.run(async (current, signal) => {
        const data = await this.loadSuggestions(signal);
        if (!current()) return;
        const profile = data.motors.find(x => x.id === id);
        const sensor = profile?.systemType === "torque_sensor";
        if (!profile || (sensor ? this.state.view !== "sensors" : this.state.view === "sensors" || this.motorBrandFor(profile) !== this.state.motorBrand)) throw new Error(sensor ? "Please choose your exact torque sensor again." : "Please choose your motor brand and exact motor again.");
        if (sensor) this.state.motorBrand = this.motorBrandFor(profile);
        Object.assign(this.state, {motor: profile.label, motorId: profile.id, systemType: sensor ? "torque_sensor" : "motor", setup: "motor-selected", suggestionProfile: null, size: "", part: null, reviewReason: ""});
        if (profile.motorLocation === "hub") {
          this.guide("conventional");
          return;
        }
        if (profile.basis === "IDENTIFICATION_ONLY" || profile.group === "UNKNOWN" || profile.blockedReason) {
          this.needsReview(sensor ? "Sensor identified" : "Motor identified", profile.friendlyLabel || profile.label, profile.blockedReason || "We have identified this motor. Use the full code from your current crank, or send photos of both crank arms so we can check the replacement and mounting details.");
          return;
        }
        if (profile.basis === "MODEL_SPECIFIC_CATALOGUE" || this.motorBrandFor(profile) === "Specialized") {
          this.needsReview("Identify your bike", this.state.bike ? `${this.state.bike.brand} ${this.state.bike.model}` : this.state.query || "Specialized bike", "This motor selection needs a model-specific left/right crank pair. Identify the bike and model year, or use the full code from the existing crank.");
          return;
        }
        const recorded = this.state.bike && data.bikes.find(x => x.bike?.id === this.state.bike.id);
        if (recorded?.blockedReason || recorded?.blockedYears?.length && (!this.validYear(this.state.year) || recorded.blockedYears.includes(Number(this.state.year)))) {
          this.needsReview("Check this bike's mounting", `${this.state.bike.brand} ${this.state.bike.model}`, recorded.blockedReason || recorded.yearBlockReason || "This bicycle needs a model-year-specific mounting check before a replacement is selected.");
          return;
        }
        const label = this.state.bike ? `${this.state.bike.brand} ${this.state.bike.model} · ${profile.friendlyLabel || profile.label}` : this.state.query ? `${this.state.query} · ${profile.friendlyLabel || profile.label}` : profile.friendlyLabel || profile.label;
        await this.showSuggestions({...profile, qLabel: null, factoryCrank: "", factoryLengths: []}, current, label, signal);
      });
    }
    motorBrandFor(profile) {
      return profile.brand || profile.label.match(/^([A-Za-z][A-Za-z0-9-]*)\b/)?.[1] || "Other";
    }
    motorStatus(profile) {
      const status = this.normalize(profile.status);
      return ["current", "legacy", "announced"].includes(status) ? status : "";
    }
    motorSearchScore(profile, query) {
      const normalizeMotor = value => this.normalize(String(value ?? "").normalize("NFKD").replaceAll("+", " plus "));
      let q = normalizeMotor(query);
      if (!q) return 0;
      const brand = normalizeMotor(this.motorBrandFor(profile));
      if (q.startsWith(`${brand} `)) q = q.slice(brand.length + 1);
      const compact = value => normalizeMotor(value).replaceAll(" ", "");
      const code = compact(q);
      const aliases = profile.aliases || [];
      if ([profile.label, profile.friendlyLabel, ...aliases].some(value => compact(value) === code)) return 4;
      if (aliases.some(alias => {
        const pattern = String(alias).toUpperCase().replace(/[\s-]+/g, "");
        return /^BDU[0-9XY]+$/.test(pattern) && new RegExp("^" + pattern.replace(/[XY]/g, "[A-Z0-9]") + "$", "i").test(code);
      })) return 3;
      const text = normalizeMotor([profile.brand, profile.label, profile.friendlyLabel, profile.generation, ...aliases].join(" "));
      return q.split(" ").every(token => text.includes(token)) ? 1 : 0;
    }
    normalize(value) {
      return String(value ?? "").replace(/[²³]/g, x => x === "²" ? "2" : "3").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    }
    searchScore(bike, query) {
      const q = this.normalize(query);
      if (!q) return 0;
      const code = this.normalize(bike.code);
      const model = this.normalize(bike.model);
      const full = this.normalize(`${bike.brand} ${bike.model}`);
      const year = this.normalize(`${bike.brand} ${bike.model} ${bike.year || ""}`);
      const compact = value => value.replaceAll(" ", "");
      if (code && compact(code) === compact(q)) return 120;
      if (compact(year) === compact(q)) return 110;
      if (compact(full) === compact(q)) return 100;
      if (compact(model) === compact(q)) return 95;
      if (full.startsWith(q)) return 85;
      if (model.startsWith(q)) return 80;
      if (model.includes(q) || compact(model).includes(compact(q))) return 70;
      return 50;
    }
    shortMotor(value) {
      return String(value || "").split(",")[0].replace(/\s+(?:custom\s+Rx|Custom\s+Rx|Rx).*$/i, "").replace(/\s+Custom\s+Tuned.*$/i, "").trim();
    }
    resultMeta(bike) {
      const primary = [bike.year || "Year not recorded", this.shortMotor(bike.motor)].filter(Boolean).map(esc).join(" · ");
      const details = [bike.code ? `Model code ${bike.code}` : "", bike.market ? `Market ${bike.market}` : ""].filter(Boolean).map(esc).join(" · ");
      return `<small>${primary}</small>${details ? `<small class="mcf-result-meta">${details}</small>` : ""}`;
    }
    stable(value) {
      if (Array.isArray(value)) return `[${value.map(x => this.stable(x)).join(",")}]`;
      if (value && typeof value === "object") return `{${Object.keys(value).sort().map(k => `${JSON.stringify(k)}:${this.stable(value[k])}`).join(",")}}`;
      return JSON.stringify(value);
    }
    validFitment(fit, bike) {
      const c = fit.context;
      const numeric = (value, min, max) => typeof value === "number" && Number.isFinite(value) && value >= min && value <= max;
      if (fit.verified !== true || !c || !fit.expected || typeof fit.expected !== "object" || c.originalOnly !== true || c.bikeId !== bike.id) return false;
      if (!Number.isInteger(c.year) || !numeric(c.year, 1990, 2100) || (bike.year && Number(bike.year) !== c.year)) return false;
      if (!numeric(c.lengthMm, 80, 250) || !numeric(c.leftOffsetMm, -100, 150) || !numeric(c.rightOffsetMm, -100, 150)) return false;
      if (![c.frameSize, c.motorReference, c.interface, c.variant?.id, c.variant?.sku, fit.variant?.handle].every(x => typeof x === "string" && x.trim())) return false;
      if (!Array.isArray(c.variant.options) || !c.variant.options.length) return false;
      const identity = Object.fromEntries(["brand", "model", "year", "code", "version", "market", "motor"].map(k => [k, bike[k] ?? null]));
      if (this.stable(identity) !== this.stable(c.bike)) return false;
      if (this.stable({id: fit.variant.id, sku: fit.variant.sku, options: fit.variant.options}) !== this.stable(c.variant)) return false;
      return this.stable(c) === this.stable(fit.expected);
    }
    async api(params, signal) {
      if (params.mode === "part" || params.mode === "motors") {
        const data = await this.loadParts(signal);
        if (params.mode === "motors") return {motors: data.motors};
        const code = this.normalize(params.q).replaceAll(" ", "");
        return {parts: code ? data.parts.filter(v => v.sku && this.normalize(v.sku).replaceAll(" ", "") === code) : [], state: "IDENTIFIED_ONLY"};
      }
      const {rows: bikes} = await this.loadNative("bikes", false, signal);
      if (params.mode === "search") {
        const tokens = this.normalize(params.q).split(" ").filter(Boolean);
        const filters = params.filters || {};
        const matches = bikes.filter(b => (!filters.brand || b.brand === filters.brand) && (!filters.year || String(b.year || "") === String(filters.year)) && tokens.every(t => {
          const text = this.normalize([b.brand, b.model, b.code, b.year, b.version, b.market, b.motor].join(" "));
          return text.includes(t) || text.replaceAll(" ", "").includes(t);
        })).map((bike, order) => ({bike, order, score: this.searchScore(bike, params.q)})).sort((a, b) => b.score - a.score || a.order - b.order).map(x => x.bike);
        const offset = Math.max(0, Math.floor(Number(params.offset) || 0));
        const actualOffset = matches.length ? Math.min(offset, Math.floor((matches.length - 1) / 12) * 12) : 0;
        return {bikes: matches.slice(actualOffset, actualOffset + 12), total: matches.length, offset: actualOffset, brands: [...new Set(bikes.map(b => b.brand).filter(Boolean))].sort((a, b) => a.localeCompare(b)), years: [...new Set(bikes.map(b => Number(b.year)).filter(year => this.validYear(year)))].sort((a, b) => b - a)};
      }
      const bike = bikes.find(b => b.id === params.id);
      if (!bike) throw new Error("Bike not found. Please search again.");
      let fitmentData = await this.loadNative("fitments", false, signal);
      if (params.mode === "resolve" && (fitmentData.enabled || fitmentData.rows.length)) fitmentData = await this.loadNative("fitments", true, signal);
      const {rows: fits, enabled} = fitmentData;
      const valid = enabled ? fits.filter(f => this.validFitment(f, bike)) : [];
      if (params.mode === "bike") return {bike, sizes: [...new Set(valid.map(f => f.context.frameSize).filter(s => s !== "ALL"))]};
      if (params.mode === "resolve") {
        const yearConfirmed = this.validYear(params.year);
        const recommendations = params.setup === "original" && yearConfirmed ? valid.filter(f => f.context.year === Number(params.year) && (f.context.frameSize === "ALL" || this.normalize(f.context.frameSize) === this.normalize(params.size))).map(f => ({ruleId: f.id, lengthMm: f.context.lengthMm, variant: f.variant})) : [];
        if (recommendations.length) return {state: "VERIFIED", recommendations};
        const suggestion = params.setup === "original" ? await this.suggestionFor(bike, params.year, signal) : null;
        return {state: suggestion?.products.length ? "SUGGESTED" : "NEEDS_REVIEW", recommendations: [], suggestion, reason: params.setup !== "original" ? "Identify the motor currently installed to see its catalogue options." : suggestion?.blockedReason || ""};
      }
      throw new Error("Please search again.");
    }
    async run(work) {
      this.controller?.abort();
      this.controller = new AbortController();
      const sequence = ++this.sequence;
      this.status("Loading…");
      this.setAttribute("aria-busy", "true");
      const controller = this.controller;
      this.retryWork = null;
      let timedOut = false;
      const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 30000);
      try {
        await work(() => sequence === this.sequence && this.isConnected && !controller.signal.aborted, controller.signal);
      } catch (error) {
        if (sequence === this.sequence && this.isConnected) {
          this.status(timedOut ? "The request took too long. Please try again." : error.name === "AbortError" ? "The request was interrupted. Please try again." : error.message || "We could not complete the request. Please try again.");
          this.retryWork = work;
          const retry = document.createElement("button");
          retry.type = "button";
          retry.className = "mcf-retry";
          retry.dataset.action = "retry";
          retry.textContent = "Try again";
          this.querySelector(".mcf-status")?.append(" ", retry);
        }
      } finally {
        clearTimeout(timeout);
        if (sequence === this.sequence) {
          this.removeAttribute("aria-busy");
          if (this.querySelector(".mcf-status")?.textContent === "Loading…")
            this.status("");
        }
      }
    }

    brandCards(brands) {
      return `<div class="mcf-motor-grid">${brands.map(brand => `<button class="mcf-motor-card" data-action="motor-brand" data-brand="${esc(brand)}" type="button"><b>${esc(brand === "DJI" ? "DJI / Avinox" : brand)}</b><small>Identify my motor <span aria-hidden="true">↗</span></small></button>`).join("")}</div>`;
    }
    context() {
      const b = this.state.bike;
      if (!b && !this.state.motor && !this.state.motorMarking) return "";
      return `<div class="mcf-context">${b ? `<b>${esc(b.brand)} ${esc(b.model)}</b>` : ""}${this.state.motor ? `<span>Selected ${this.state.systemType === "torque_sensor" ? "sensor" : "motor"}: ${esc(this.state.motor)}</span>` : b?.motor ? `<span>Recorded motor: ${esc(b.motor)}</span>` : ""}${this.state.motorMarking ? `<span>Your motor marking: ${esc(this.state.motorMarking)}</span>` : ""}</div>`;
    }
    home() {
      this.cancel();
      this.state = {view: "home", query: "", bike: null, year: "", size: "", setup: "", resultsOffset: 0, searchFilters: {brand: "", year: ""}, partQuery: "", motor: "", motorBrand: "", motorId: "", motorMarking: "", motorFilter: "all", sensorMarking: ""};
      const brands = ["Bosch", "Shimano", "Bafang", "Brose", "Yamaha", "TQ", "Panasonic", "Specialized", "DJI", "ZF", "Fazua", "Mahle", "Maxon", "OLI", "Pendix", "QORE"];
      for (const motor of this.suggestionsCache?.motors || []) if (!motor.hiddenLegacyProfile && motor.systemType !== "torque_sensor" && !brands.includes(this.motorBrandFor(motor))) brands.push(this.motorBrandFor(motor));
      this.shell(`${this.heading("FIND YOUR CRANKS", "Which motor<br>does your bike use?", "Start with the name on your motor.")}${this.brandCards(brands)}<div class="mcf-route-links"><button type="button" data-action="motor-guide">I don't know — help me identify it ↗</button><button type="button" data-action="conventional">No motor, or a motor in the wheel ↗</button><button type="button" data-action="sensors">Identify a bottom bracket torque sensor ↗</button><button type="button" data-action="motor-brands">Other motor brands ↗</button></div><details class="mcf-search-card"><summary>Know your bike model or Miranda part code?</summary><form data-form="search" role="search"><label class="mcf-label" for="mcf-home-search">Bike brand, model or motor</label><div class="mcf-search"><input id="mcf-home-search" name="q" type="search" aria-label="Bike brand, model or motor" placeholder="E.g. KTM Bosch or Merida EP6" maxlength="200" autocomplete="off"><button class="mcf-button" type="submit">Find</button></div></form><div class="mcf-route-links"><button data-action="search" type="button">Browse bikes ↗</button><button data-action="part" type="button">Use a Miranda part code ↗</button></div></details>`, "home");
    }
    search(query = "", offset = 0, filters = {}) {
      const selectedFilters = {brand: String(filters.brand || ""), year: String(filters.year || "")};
      Object.assign(this.state, {query, resultsOffset: offset, searchFilters: selectedFilters, motorMarking: "", bike: null, year: "", size: "", sizes: [], setup: "", motor: "", motorBrand: "", motorId: "", part: null, partQuery: "", suggestionProfile: null, reviewReason: ""});
      this.run(async (current, signal) => {
        const data = await this.api({ mode: "search", q: query, offset, filters: selectedFilters }, signal);
        if (!current()) return;
        this.state.view = "results";
        this.state.resultsOffset = data.offset;
        const start = data.total ? data.offset + 1 : 0;
        const end = Math.min(data.offset + data.bikes.length, data.total);
        const page = Math.floor(data.offset / 12) + 1;
        const pages = Math.max(1, Math.ceil(data.total / 12));
        const brands = data.brands.map(brand => `<option value="${esc(brand)}"${brand === selectedFilters.brand ? " selected" : ""}>${esc(brand)}</option>`).join("");
        const years = data.years.map(year => `<option value="${esc(year)}"${String(year) === selectedFilters.year ? " selected" : ""}>${esc(year)}</option>`).join("");
        this.shell(
          `${this.back()}${this.heading("01 / YOUR BIKE", "Choose your bike.", data.total ? `${data.total} matching ${data.total === 1 ? "bike" : "bikes"}` : "No matching bikes yet.")}<form data-form="search"><div class="mcf-search"><input name="q" type="search" aria-label="Search bikes" value="${esc(query)}" maxlength="200"><button class="mcf-button">Find</button></div><div class="mcf-search-filters"><label class="mcf-field">Bike brand<select name="brand"><option value="">All brands</option>${brands}</select></label><label class="mcf-field">Model year<select name="filter-year"><option value="">All years</option>${years}</select></label></div></form>${data.total ? `<p class="mcf-results-summary">Showing ${start}–${end} of ${data.total} ${data.total === 1 ? "bike" : "bikes"}</p>` : ""}<div class="mcf-results">${data.bikes.map((b) => `<button class="mcf-result" type="button" data-action="bike" data-id="${esc(b.id)}"><span><b>${esc(b.brand)} ${esc(b.model)}</b>${this.resultMeta(b)}</span><span aria-hidden="true">↗</span></button>`).join("")}</div><div class="mcf-pagination">${data.offset ? `<button type="button" data-action="page" data-offset="${Math.max(0, data.offset - 12)}">← Previous</button>` : ""}${data.total ? `<span class="mcf-page-info">Page ${page} of ${pages}</span>` : ""}${data.offset + 12 < data.total ? `<button type="button" data-action="page" data-offset="${data.offset + 12}">Next →</button>` : ""}</div>${!data.total ? '<div class="mcf-review"><h3>Know your current motor?</h3><p>Identify the motor already fitted to your bike, or use the full code from your current Miranda crank.</p><div class="mcf-choices"><button type="button" data-action="motor">Identify my motor ↗</button><button type="button" data-action="part">Use my current part code ↗</button></div></div>' : ""}<div class="mcf-route-links"><button type="button" data-action="motor">Continue by motor ↗</button><button type="button" data-action="motor-guide">Help identify my motor ↗</button></div>`,
        );
        this.focus();
      });
    }
    chooseBike(id) {
      this.run(async (current, signal) => {
        const data = await this.api({ mode: "bike", id }, signal);
        if (!current()) return;
        this.state.bike = data.bike;
        this.state.year = data.bike.year || "";
        this.state.sizes = data.sizes;
        Object.assign(this.state, {size: "", setup: "", motor: "", motorBrand: "", motorId: "", part: null, partQuery: "", suggestionProfile: null, reviewReason: ""});
        this.setup();
      });
    }

    setup() {
      this.captureDraft();
      this.cancel();
      const b = this.state.bike;
      if (!b) { this.home(); return; }
      this.state.view = "setup";
      const sizeField = this.state.sizes?.length ? `<label class="mcf-field">Frame size<select name="size"><option value="">Select size</option>${this.state.sizes.map(s => `<option value="${esc(s)}"${s === this.state.size ? " selected" : ""}>${esc(s)}</option>`).join("")}<option value="unknown">Not sure</option></select></label>` : "";
      this.shell(`${this.back("results")}${this.heading("YOUR BIKE", "Is this the motor<br>on your bike?", "Use the recorded setup, or identify the motor currently fitted.")}<div class="mcf-selected"><b>${esc(b.brand)} ${esc(b.model)}</b><span>${esc(b.motor || "Motor not recorded")}</span></div><form data-form="setup"><details class="mcf-factory"><summary>Year and frame size — optional</summary><label class="mcf-field">Model year<input name="year" type="number" min="1990" max="2100" step="1" value="${esc(this.state.year)}" placeholder="Leave blank if unsure"></label>${sizeField}</details><div class="mcf-choices"><button name="setup" value="original" type="submit">Yes, motor and cranks are original <span>↗</span></button><button name="setup" value="modified" type="submit" formnovalidate>I have changed the motor or cranks <span>↗</span></button><button name="setup" value="unknown" type="submit" formnovalidate>I'm not sure — identify my motor <span>↗</span></button></div></form><p class="mcf-note">You can view motor-family options without knowing the year. Some model-specific replacements need one extra check.</p>`, "setup");
      this.focus();
    }
    resolve() {
      if (!this.state.bike) { this.search(this.state.query, this.state.resultsOffset, this.state.searchFilters); return; }
      this.run(async (current, signal) => {
        const result = await this.api({
          mode: "resolve",
          id: this.state.bike.id,
          year: this.state.year,
          size: this.state.size,
          setup: this.state.setup,
        }, signal);
        if (!current()) return;
        if (result.state === "SUGGESTED") {
          await this.showSuggestions(result.suggestion, current, `${this.state.bike.brand} ${this.state.bike.model}`, signal);
          return;
        }
        if (result.state !== "VERIFIED" || !result.recommendations?.length) {
          this.needsReview(
            "Bike found",
            `${this.state.bike.brand} ${this.state.bike.model}`,
            result.suggestion?.blockedReason || result.reason,
          );
          return;
        }
        const cards = await Promise.all(
          result.recommendations.map(async (rec) => {
            const v = rec.variant;
            const id = String(v.id).split("/").pop();
            let live = null;
            let failure = null;
            try {
              const checked = await this.liveProduct(v.handle, [v], signal);
              live = checked?.variants[0];
            } catch (error) {
              if (error.name === "AbortError") throw error;
              failure = error;
            }
            const matches = Boolean(live);
            const available = matches && live.available === true;
            return {
              matches,
              failure,
              html: `<article class="mcf-recommendation"><span class="mcf-tag">${matches ? "FIT VERIFIED" : "EXACT VARIANT CHECK NEEDED"}</span><h3>${esc(v.productTitle)}</h3><p>${esc(v.title)} · ${esc(rec.lengthMm)} mm</p>${available ? `<a class="mcf-button" href="${esc(this.dataset.root || "/")}products/${encodeURIComponent(v.handle)}?variant=${encodeURIComponent(id)}">View cranks ↗</a>` : `<p>${matches ? "Currently unavailable. Contact us for help." : "We could not confirm this exact variant. Please contact us."}</p><button class="mcf-button" type="button" data-action="help">Check with Miranda</button>`}</article>`,
            };
          }),
        );
        if (!current()) return;
        if (!cards.some(c => c.matches) && cards.some(c => c.failure)) throw new Error("We could not check the live catalogue. Please try again.");
        this.state.view = "recommendations";
        this.shell(
          `${this.back("setup")}${this.heading("03 / YOUR CRANKS", cards.some((c) => c.matches) ? "Made for your setup." : "Let’s check this option.", cards.some((c) => c.matches) ? "Choose your cranks to see the current price and purchase options." : "Please ask us to confirm the exact variant before ordering.")}<div class="mcf-recommendations">${cards.map((c) => c.html).join("")}</div>`,
        );
        this.focus();
      });
    }

    needsReview(label, title, reason = "") {
      this.cancel();
      this.state.view = "review";
      Object.assign(this.state, {reviewReason: reason, reviewLabel: label, reviewTitle: title});
      const bike = this.state.bike;
      const sensor = this.state.systemType === "torque_sensor";
      const identifyBike = this.state.motorBrand === "Specialized" ? '<button type="button" data-action="identify-bike">Identify my bike model ↗</button>' : "";
      this.shell(`${this.back(sensor ? "sensors" : bike ? "setup" : this.state.motorBrand ? "motor" : this.state.query ? "results" : "home")}${this.heading(label.toUpperCase(), esc(title))}${this.context()}<div class="mcf-option-card mcf-review"><h3>Let's find the missing detail.</h3><p>${esc(reason || "The existing crank reference or motor marking will help us find the right next option.")}</p><div class="mcf-choices">${identifyBike}<button type="button" data-action="${sensor ? "sensors" : "motor"}">Identify or correct the installed ${sensor ? "sensor" : "motor"} ↗</button><button type="button" data-action="part">Use my existing part code ↗</button><button type="button" data-action="${sensor ? "geometry-guide" : "motor-guide"}">Show me where to look ↗</button></div><button class="mcf-button" type="button" data-action="help">Ask Miranda to identify it ↗</button></div>`, "review");
      this.focus();
    }
    part() {
      this.captureDraft();
      if (this.state.view !== "part" && this.state.view !== "part-result" && this.state.view !== "help") this.state.partReturn = this.state.view;
      this.cancel();
      this.state.view = "part";
      this.shell(
        `${this.back("part-origin")}${this.heading("PART CODE", "Find your Miranda part.", "Enter the full code from the part or packaging.")}${this.state.bike ? `<div class="mcf-selected"><b>${esc(this.state.bike.brand)} ${esc(this.state.bike.model)}</b><span>${this.bikeMeta(this.state.bike)}</span></div>` : ""}<form data-form="part" class="mcf-search"><input name="q" aria-label="Full Miranda part code" placeholder="Full part code" value="${esc(this.state.partQuery || "")}" maxlength="120" required><button class="mcf-button">Find</button></form><p class="mcf-note">A Q-factor label or crank length is not a full part code.</p>`,
      );
      this.focus();
    }
    findPart(query) {
      this.state.partQuery = String(query || "");
      this.state.part = null;
      this.run(async (current, signal) => {
        const data = await this.api({ mode: "part", q: query }, signal);
        if (!current()) return;
        if (data.parts.length !== 1) {
          this.status(
            data.parts.length
              ? "This code matches more than one part. Please ask us to confirm."
              : "Part not found. Check the full code or ask us for help.",
          );
          return;
        }
        const p = data.parts[0];
        const checked = await this.liveProduct(p.handle, [p], signal);
        if (!current()) return;
        const variant = checked?.variants[0];
        if (!variant) {
          this.needsReview("Part code found", `${p.productTitle} · ${p.title}`);
          this.status("The catalogue part has changed. Please ask us to check the current replacement.");
          return;
        }
        this.state.part = p;
        this.state.view = "part-result";
        const url = `${this.dataset.root || "/"}products/${encodeURIComponent(p.handle)}?variant=${encodeURIComponent(variant.id)}`;
        this.shell(`${this.back("part")}${this.heading("EXACT PART CODE", esc(p.productTitle), p.title)}<div class="mcf-recommendation"><span class="mcf-tag">CATALOGUE PART IDENTIFIED</span><p>${esc(p.sku)}</p><p class="mcf-note">This code identifies the same catalogue part. Check that it is the full code from the part you are replacing.</p>${variant.available ? `<a class="mcf-button" href="${esc(url)}">View this exact part ↗</a>` : "<p>Currently unavailable.</p><button class='mcf-button' type='button' data-action='help'>Ask about a replacement ↗</button>"}</div>`);
        this.focus();
      });
    }

    sensors() {
      this.captureDraft(false);
      this.run(async (current, signal) => {
        const data = await this.loadSuggestions(signal);
        if (!current()) return;
        if (this.state.systemType !== "torque_sensor") Object.assign(this.state, {motor: "", motorId: "", suggestionProfile: null});
        Object.assign(this.state, {view: "sensors", systemType: "torque_sensor", motorBrand: ""});
        const query = String(this.state.sensorMarking || "").trim();
        const profiles = data.motors.filter(m => !m.hiddenLegacyProfile && m.systemType === "torque_sensor");
        const scored = profiles.map(profile => ({profile, score: query ? this.motorSearchScore(profile, query) : 1}));
        const exact = scored.some(item => item.score === 4);
        const matches = scored.filter(item => exact ? item.score === 4 : item.score > 0).map(item => item.profile);
        let images = {};
        try { images = JSON.parse(this.dataset.motorImages || "{}"); } catch {}
        const cards = matches.map(m => {
          const image = images[m.imageAsset] || m.imageUrl;
          return `<button class="mcf-option-card" type="button" data-action="choose-motor" data-id="${esc(m.id)}">${image ? `<img src="${esc(image)}" alt="${esc(m.imageAlt || m.label)}" width="96" height="72" loading="lazy">` : ""}<span><b>${esc(m.label)}</b>${m.helpText ? `<small>${esc(m.helpText)}</small>` : ""}</span><span aria-hidden="true">↗</span></button>`;
        }).join("");
        this.shell(`${this.back("home")}${this.heading("02 / YOUR TORQUE SENSOR", "Which torque sensor is fitted?", "Choose the name on the sensor around the crank axle, or use the full code from your existing crank.")}${this.context()}<form data-form="sensor-marking" role="search"><label class="mcf-field">Sensor name or reference — optional<div class="mcf-search"><input name="marking" value="${esc(query)}" maxlength="120" placeholder="E.g. SR PA211 or AUTORQ ISIS"><button class="mcf-button">Find</button></div></label></form><p class="mcf-motor-count" aria-live="polite">${matches.length} ${matches.length === 1 ? "sensor" : "sensors"}${query ? " matching your search" : " listed"}.</p><div class="mcf-choices">${cards}</div>${!matches.length ? '<p class="mcf-note">We have not identified this sensor reference yet. Use your existing crank code or ask Miranda to check.</p>' : ""}<div class="mcf-route-links">${query ? '<button type="button" data-action="clear-sensor-marking">Show all torque sensors ↗</button>' : ""}<button type="button" data-action="part">Use my existing crank code ↗</button><button type="button" data-action="help">Ask Miranda to identify it ↗</button><button type="button" data-action="motor-brands">Identify a central motor instead ↗</button></div>`, "sensors");
        this.focus();
      });
    }
    motors(brand = this.state.motorBrand || "") {
      this.captureDraft(false);
      this.run(async (current, signal) => {
        const data = await this.loadSuggestions(signal);
        if (!current()) return;
        const visibleProfiles = data.motors.filter(m => !m.hiddenLegacyProfile && m.systemType !== "torque_sensor");
        if (this.state.systemType === "torque_sensor") Object.assign(this.state, {motor: "", motorId: "", suggestionProfile: null});
        this.state.systemType = "motor";
        const brands = [...new Set(visibleProfiles.map(m => this.motorBrandFor(m)))];
        if (brand && !brands.includes(brand)) {
          this.state.motorBrand = brand;
          this.guide("motor");
          return;
        }
        if (brand !== this.state.motorBrand) this.state.motorFilter = "all";
        this.state.motorBrand = brand;
        this.state.view = "motor";
        const query = String(this.state.motorMarking || "").trim();
        const profiles = visibleProfiles.filter(m => (!brand || this.motorBrandFor(m) === brand));
        const statusOrder = {announced: 0, current: 1, "": 2, legacy: 3};
        const ordered = profiles.map((profile, index) => ({profile, index})).sort((a, b) => {
          if (Number.isFinite(a.profile.sortOrder) && Number.isFinite(b.profile.sortOrder) && a.profile.sortOrder !== b.profile.sortOrder) return a.profile.sortOrder - b.profile.sortOrder;
          const status = statusOrder[this.motorStatus(a.profile)] - statusOrder[this.motorStatus(b.profile)];
          if (status) return status;
          const year = (Number(b.profile.releaseYear) || 0) - (Number(a.profile.releaseYear) || 0);
          return year || a.index - b.index;
        }).map(item => item.profile);
        let matches = ordered;
        if (query) {
          const scored = ordered.map(profile => ({profile, score: this.motorSearchScore(profile, query)}));
          const exact = scored.some(item => item.score === 4);
          matches = scored.filter(item => exact ? item.score === 4 : item.score > 0).sort((a, b) => b.score - a.score).map(item => item.profile);
        }
        const searchMatches = matches;
        const filter = ["current", "legacy", "announced"].includes(this.state.motorFilter) ? this.state.motorFilter : "all";
        this.state.motorFilter = filter;
        if (filter !== "all") matches = matches.filter(m => this.motorStatus(m) === filter);
        const statusLabels = {current: "Current / documented", legacy: "Earlier models", announced: "Announced models"};
        const statuses = ["current", "legacy", "announced"].filter(status => profiles.some(m => this.motorStatus(m) === status));
        const filterChoices = statuses.length ? `<div class="mcf-motor-filters" role="group" aria-label="Motor generations">${["all", ...statuses].map(status => `<button type="button" data-action="motor-filter" data-filter="${status}" aria-pressed="${filter === status}">${status === "all" ? "All models" : statusLabels[status]}</button>`).join("")}</div>` : "";
        let motorImages = {};
        try { motorImages = JSON.parse(this.dataset.motorImages || "{}"); } catch {}
        const choices = matches.map(m => ({...m, imageUrl: motorImages[m.imageAsset] || m.imageUrl})).map(m => {
          const status = this.motorStatus(m);
          const meta = [m.generation, status === "legacy" ? "Earlier model" : status === "announced" ? "Announced model" : ""].filter(Boolean);
          return `<button class="mcf-option-card" type="button" data-action="choose-motor" data-id="${esc(m.id)}">${m.imageUrl ? `<img src="${esc(m.imageUrl)}" alt="${esc(m.imageAlt || `${m.friendlyLabel || m.label} motor`)}" width="96" height="72" loading="lazy">` : ""}<span><b>${esc(m.friendlyLabel || m.label.split(" · ")[0])}</b>${meta.length ? `<small class="mcf-motor-meta">${meta.map(esc).join(" · ")}</small>` : ""}${m.helpText ? `<small>${esc(m.helpText)}</small>` : ""}</span><span aria-hidden="true">↗</span></button>`;
        }).join("");
        const noMatches = !matches.length ? `<div class="mcf-option-card"><p>${searchMatches.length ? "This name is listed in another generation. Show all models to find it." : "We have not identified this marking yet. Check the label or send us a photo so we can identify it."}</p>${searchMatches.length ? '<button class="mcf-button" type="button" data-action="motor-filter" data-filter="all">Show all generations ↗</button>' : '<button class="mcf-button" type="button" data-action="motor-guide">Help me identify it ↗</button>'}</div>` : "";
        const selection = brand ? `<form data-form="motor-marking" role="search"><label class="mcf-field">Motor name or reference — optional<div class="mcf-search"><input name="marking" value="${esc(this.state.motorMarking || "")}" maxlength="120" placeholder="E.g. CX Race, EP6, M410 or BDU…"><button class="mcf-button">Find</button></div></label></form>${filterChoices}<p class="mcf-motor-count" aria-live="polite">${matches.length} ${matches.length === 1 ? "motor" : "motors"}${query ? " matching your search" : " listed"}.${filter === "all" && statuses.includes("current") && statuses.includes("legacy") ? " Includes recent models and earlier generations." : ""}</p><div class="mcf-choices">${choices}</div>${noMatches}` : this.brandCards(brands);
        this.shell(`${this.back(brand ? "motor-brands" : this.state.bike ? "setup" : this.state.query ? "results" : "home")}${this.heading("02 / YOUR MOTOR", brand ? `Which ${esc(brand === "DJI" ? "DJI / Avinox" : brand)} motor?` : "What name is on the motor?", brand ? "Choose the name or reference on the motor already fitted to your bike." : "Read the name near the pedals. The bike brand can be different.")}${this.context()}${selection}<div class="mcf-route-links"><button type="button" data-action="motor-guide">I don't know / mine isn't listed ↗</button>${brand && (this.state.motorMarking || filter !== "all") ? '<button type="button" data-action="clear-motor-marking">Show all motors for this brand ↗</button>' : ""}<button type="button" data-action="part">Use my current crank code ↗</button></div>`, "motor");
        this.focus();
      });
    }
    guide(kind = "motor") {
      this.captureDraft();
      if (this.state.view !== "guide") this.state.guideReturn = this.state.view;
      this.cancel();
      this.state.view = "guide";
      this.state.guideKind = kind;
      const geometry = kind === "geometry";
      const conventional = kind === "conventional";
      const title = geometry ? "Find your crank's markings." : conventional ? "Let's identify your crankset." : "Find the name on your motor.";
      const description = geometry ? "A few visible details help you choose the right version." : conventional ? "With no motor at the pedals, the crankset and bottom bracket determine the replacement." : "You can do this with the bike fully assembled.";
      const steps = geometry ? '<li><b>Look on the inside of each crank.</b> Find a length such as 165 or 170 mm, and any model or Q marking.</li><li><b>Check both sides.</b> The left and right arms can have different shapes or markings.</li><li><b>If there is no clear code, take photos.</b> Include both crank arms, their fixing area and the space between each arm and the frame.</li>' : conventional ? '<li><b>Look for a model or reference on the crank.</b> It is often on the inside of the arm.</li><li><b>Decide what you are replacing.</b> A single arm, a left/right pair or the complete crankset may need different checks.</li><li><b>Photograph the assembly.</b> Show both sides and the area around the bottom bracket. There is no need to dismantle it to ask for help.</li>' : '<li><b>Look around the pedals.</b> Read the brand on the central motor casing. A motor inside a wheel uses a different crankset route.</li><li><b>Look for a name or reference.</b> Check the visible label, the bike specification or your motor app. Examples include EP6 or M410.</li><li><b>Still unsure? Photograph the label.</b> Include the motor and both crank arms. We can help identify the markings.</li>';
      const collections = conventional ? `<div class="mcf-route-links"><a href="${esc(this.dataset.root || "/")}collections/mtb-cranksets">Browse MTB cranksets ↗</a><a href="${esc(this.dataset.root || "/")}collections/road-gravel-cranksets">Browse road & gravel cranksets ↗</a><a href="${esc(this.dataset.root || "/")}collections/city-trekking-cranksets">Browse city & trekking cranksets ↗</a></div><p class="mcf-note">Browse by riding style, then confirm the bottom bracket and mounting before ordering.</p>` : "";
      const actions = geometry ? '<button class="mcf-button" type="button" data-action="part">I found a full Miranda code ↗</button><button type="button" data-action="guide-origin">Back to my options ↗</button>' : conventional ? '<button class="mcf-button" type="button" data-action="part">Use a Miranda part code ↗</button>' : '<button class="mcf-button" type="button" data-action="motor">I found the motor brand ↗</button><button type="button" data-action="conventional">The motor is in a wheel / no motor ↗</button>';
      this.shell(`${this.back("guide-origin")}${this.heading("IDENTIFICATION HELP", title, description)}${this.context()}<div class="mcf-guide"><ol class="mcf-guide-steps">${steps}</ol>${collections}<div class="mcf-route-links">${actions}</div><button class="mcf-button" type="button" data-action="help">Ask Miranda with photos ↗</button></div>`, "guide");
      this.focus();
    }
    help() {
      this.captureDraft();
      if (this.state.view !== "help") this.state.helpReturn = this.state.view;
      this.cancel();
      this.state.view = "help";
      const b = this.state.bike;
      this.state.supportText = [
        "Miranda Crank Finder — setup check",
        b
          ? `Bike: ${b.brand} ${b.model}`
          : this.state.query
            ? `Bike search: ${this.state.query}`
            : "Bike: ",
        b?.code ? `Bike code: ${b.code}` : "",
        b?.market ? `Market: ${b.market}` : "",
        `Model year: ${this.validYear(this.state.year) ? this.state.year : "Not confirmed"}`,
        `Frame size: ${this.state.size || "Not confirmed"}`,
        `Setup: ${this.state.setup || "Not confirmed"}`,
        this.state.guideKind === "conventional" ? "Crankset type: no central motor / motor in wheel" : "",
        this.state.motorMarking ? `Motor marking entered: ${this.state.motorMarking}` : "",
        b?.motor ? `Recorded motor: ${b.motor}` : "",
        this.state.part ? `Part: ${this.state.part.sku}` : "",
        this.state.partQuery && !this.state.part ? `Part code entered: ${this.state.partQuery}` : "",
        this.state.motor ? ` ${this.state.systemType === "torque_sensor" ? "Torque sensor" : "Motor"}: ${this.state.motor}`.trim() : "",
        this.state.reviewReason ? `Check needed: ${this.state.reviewReason}` : "",
        "Please confirm the compatible crank pair, length, crank geometry and hardware.",
        "I will attach photos of the motor label and both crank arms.",
      ]
        .filter(Boolean)
        .join("\n");
      let support = "";
      try {
        const url = new URL(this.dataset.support, location.origin);
        if (
          this.dataset.support &&
          (url.protocol === "https:" || url.origin === location.origin)
        )
          support = `<a class="mcf-button" href="${esc(url.href)}">Contact Miranda ↗</a>`;
      } catch {}
      this.shell(
        `${this.back("help-origin")}${this.heading("IDENTIFICATION REQUEST", "Let’s identify your setup.", "Your details are ready. Add photos when you open your email.")}<div class="mcf-help-card"><ol><li>A clear photo of the motor name or label, if present</li><li>A clear photo of each crank, including the inside markings</li><li>Bike model and year, only if you know them</li></ol><details><summary>Your setup details</summary><textarea class="mcf-support-text" aria-label="Setup details" rows="8">${esc(this.state.supportText)}</textarea></details><div class="mcf-support-actions"><a class="mcf-button" href="mailto:info@miranda.pt?subject=${encodeURIComponent("Help identify my Miranda cranks")}&amp;body=${encodeURIComponent(this.state.supportText)}">Open email and attach photos ↗</a><button data-action="copy" type="button">Copy setup details</button>${support}</div><p class="mcf-note">This opens your email app. Add the photos and send when ready. Nothing is sent by the Finder.</p></div>`,
      );
      this.focus();
    }

    returnTo(view) {
      if (view === "results") this.search(this.state.query, this.state.resultsOffset, this.state.searchFilters);
      else if (view === "setup") this.setup();
      else if (view === "part") this.part();
      else if (view === "part-result" && this.state.partQuery) this.findPart(this.state.partQuery);
      else if (view === "motor") this.motors(this.state.motorBrand);
      else if (view === "sensors") this.sensors();
      else if (view === "guide") this.guide(this.state.guideKind);
      else if (view === "review") this.needsReview(this.state.reviewLabel || "Setup check", this.state.reviewTitle || "Your bike", this.state.reviewReason);
      else if (view === "suggestions" && this.state.suggestionProfile) this.run((current, signal) => this.showSuggestions(this.state.suggestionProfile, current, this.state.suggestionLabel || this.state.motor, signal));
      else if (view === "recommendations" && this.state.bike) this.resolve();
      else this.home();
    }
    click(event) {
      const button = event.target.closest("[data-action]");
      if (!button || !this.contains(button)) return;
      this.captureDraft();
      const action = button.dataset.action;
      if (action === "home") this.home();
      if (action === "help") this.help();
      if (action === "part") this.part();
      if (action === "motor") this.motors();
      if (action === "sensors") this.sensors();
      if (action === "clear-sensor-marking") { this.state.sensorMarking = ""; this.sensors(); }
      if (action === "motor-brand") { this.state.motorMarking = ""; this.state.motorFilter = "all"; this.motors(button.dataset.brand); }
      if (action === "motor-guide") this.guide("motor");
      if (action === "geometry-guide") this.guide("geometry");
      if (action === "conventional") this.guide("conventional");
      if (action === "guide-origin") this.returnTo(this.state.guideReturn || "home");
      if (action === "clear-motor-marking") { this.state.motorMarking = ""; this.state.motorFilter = "all"; this.motors(); }
      if (action === "motor-brands") { this.state.motorMarking = ""; this.state.motorFilter = "all"; this.motors(""); }
      if (action === "motor-filter") { this.state.motorFilter = button.dataset.filter; this.motors(); }
      if (action === "identify-bike") this.search(this.state.query || "Turbo Levo");
      if (action === "help-origin") this.returnTo(this.state.helpReturn);
      if (action === "part-origin") this.returnTo(this.state.partReturn || (this.state.bike ? "setup" : "home"));
      if (action === "retry" && this.retryWork) this.run(this.retryWork);
      if (action === "unknown-year") {
        this.state.year = "";
        this.state.setup = "original";
        this.resolve();
      }
      if (action === "search") this.search("");
      if (action === "example") this.search(button.dataset.query);
      if (action === "results") this.search(this.state.query, this.state.resultsOffset, this.state.searchFilters);
      if (action === "page")
        this.search(this.state.query, Number(button.dataset.offset), this.state.searchFilters);
      if (action === "bike") this.chooseBike(button.dataset.id);
      if (action === "setup") this.setup();
      if (action === "choose-motor") {
        this.chooseMotor(button.dataset.id);
      }
      if (action === "copy") {
        const text = this.querySelector(".mcf-support-text").value;
        const sequence = this.sequence;
        if (navigator.clipboard?.writeText)
          navigator.clipboard
            .writeText(text)
            .then(() => { if (sequence === this.sequence) this.status("Setup details copied."); })
            .catch(() => { if (sequence === this.sequence) this.selectSupport(); });
        else this.selectSupport();
      }
    }
    selectSupport() {
      const input = this.querySelector(".mcf-support-text");
      if (!input) return;
      input.closest("details").open = true;
      input.select();
      this.status("Select and copy these details.");
    }
    submit(event) {
      const form = event.target.closest("form");
      if (!form) return;
      event.preventDefault();
      this.captureDraft();
      const data = new FormData(form);
      if (form.dataset.form === "search")
        this.search(String(data.get("q") || ""), 0, {brand: data.get("brand"), year: data.get("filter-year")});
      if (form.dataset.form === "part")
        this.findPart(String(data.get("q") || ""));
      if (form.dataset.form === "motor-brand") this.motors(String(data.get("brand") || ""));
      if (form.dataset.form === "sensor-marking") {
        this.state.sensorMarking = String(data.get("marking") || "").trim();
        this.sensors();
      }
      if (form.dataset.form === "motor-marking") {
        this.state.motorMarking = String(data.get("marking") || "").trim();
        this.motors();
      }
      if (form.dataset.form === "setup") {
        this.state.year = data.has("year") ? String(data.get("year") || "") : this.state.bike.year || "";
        this.state.size = data.get("size") || "";
        this.state.setup = event.submitter?.value || "unknown";
        if (!this.validYear(this.state.year)) this.state.year = "";
        if (this.state.setup !== "original") {
          this.state.suggestionProfile = null;
          this.state.motorMarking = "";
          this.motors("");
          return;
        }
        this.resolve();
      }
    }
  }
  customElements.define("miranda-native-crank-finder", CrankFinder);
})();

